import React, { useRef, useState } from "react";
import { CheckCheck, ArrowUpRight } from "lucide-react";

const THRESHOLD = 0.33;

// Swipe (touch) or click-and-drag (mouse) a row left to mark it read; with onSwipedRight, swipe right to open.
// Short drags snap back; past ~1/3 of the width the row slides away and the callback fires. Taps still reach the row.
export default function SwipeToRead({ enabled = true, onSwiped, onSwipedRight, children, testId }) {
  const ref = useRef(null);
  const drag = useRef(null);
  const moved = useRef(false);
  const [dx, setDx] = useState(0);
  const [gone, setGone] = useState(false);
  const [anim, setAnim] = useState(false);

  if (!enabled) return children;

  const down = (e) => {
    if (gone || (e.pointerType === "mouse" && e.button !== 0)) return;
    drag.current = { x: e.clientX, y: e.clientY, horizontal: null, id: e.pointerId };
    moved.current = false;
    setAnim(false);
  };
  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const mx = e.clientX - d.x;
    const my = e.clientY - d.y;
    if (d.horizontal === null && (Math.abs(mx) > 8 || Math.abs(my) > 8)) {
      d.horizontal = Math.abs(mx) > Math.abs(my);
      if (d.horizontal) ref.current?.setPointerCapture?.(d.id);
    }
    if (!d.horizontal) return;
    moved.current = true;
    setDx(onSwipedRight ? mx : Math.min(0, mx));
  };
  const finish = (toX, cb) => { setDx(toX); setGone(true); setTimeout(cb, 220); };
  const up = () => {
    const d = drag.current;
    drag.current = null;
    if (!d?.horizontal) return;
    const width = ref.current?.offsetWidth || 1;
    setAnim(true);
    if (-dx > width * THRESHOLD) finish(-width * 1.1, onSwiped);
    else if (onSwipedRight && dx > width * THRESHOLD) finish(width * 1.1, onSwipedRight);
    else setDx(0);
  };
  const width = ref.current?.offsetWidth || 1;
  const revealLeft = Math.min(1, Math.max(0, -dx) / (width * THRESHOLD));
  const revealRight = Math.min(1, Math.max(0, dx) / (width * THRESHOLD));

  return (
    <div className={`relative overflow-hidden rounded-xl transition-[max-height,opacity,margin] duration-300 ${gone ? "max-h-0 opacity-0" : "max-h-[600px]"}`} data-testid={testId} data-swiped={gone ? "true" : undefined}>
      <div className="absolute inset-0 flex items-center justify-end rounded-xl bg-emerald-500 pr-5 text-sm font-bold text-white" style={{ opacity: revealLeft }} aria-hidden>
        <CheckCheck className="mr-1.5 h-4 w-4" /> Read
      </div>
      {onSwipedRight && (
        <div className="absolute inset-0 flex items-center justify-start rounded-xl bg-sky-500 pl-5 text-sm font-bold text-white" style={{ opacity: revealRight }} aria-hidden>
          Open <ArrowUpRight className="ml-1.5 h-4 w-4" />
        </div>
      )}
      <div ref={ref} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
        onClickCapture={(e) => { if (moved.current) { e.preventDefault(); e.stopPropagation(); moved.current = false; } }}
        className={`relative select-none rounded-xl bg-white ${anim ? "transition-transform duration-200 ease-out" : ""}`}
        style={{ transform: `translateX(${dx}px)`, touchAction: "pan-y" }}>
        {children}
      </div>
    </div>
  );
}
